import React, { useState, useEffect } from "react";

/**
 * Logo Variants Preview — 3 custom V glyph mocks
 *
 *  Variant 1  Geometric V         — clean miter, chunky stroke, mathematical
 *  Variant 2  Motion V (Forward)  — right stroke extends past join as an arrow tail
 *  Variant 3  Ledger V            — multiple strokes converging to a single point (six players, one ledger)
 *
 * The current production lockup (font character "v" inside a square) is shown
 * first as a baseline for comparison.
 */

const BRAND_FG = "#0c1612";
const BRAND_ACCENT = "#dcff3a";

// ─────────────────────────── GLYPHS ────────────────────────────
// All glyphs are drawn on a 32×32 viewBox, occupying ~68% of the
// canvas. Stroke-only paths so the same SVG can scale to favicon size.
// `strokeWidth` is auto-tuned per variant for optical consistency.

function GlyphBaseline() {
  // Current production: font character "v" inside the box (rendered as text).
  return (
    <span
      className="font-bold leading-none"
      style={{ color: BRAND_ACCENT, fontSize: "0.875em" }}
      aria-label="baseline-v-font"
    >
      v
    </span>
  );
}

function GlyphGeometric({ size = 32, color = BRAND_ACCENT, stroke = 3.6 }) {
  // Chunky geometric V. Miter join → sharp point at the bottom.
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} fill="none" aria-label="geometric-v">
      <path
        d="M7 9 L16 23 L25 9"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
    </svg>
  );
}

function GlyphMotion({ size = 32, color = BRAND_ACCENT, stroke = 3.4 }) {
  // V with the right stroke continuing past the top-right corner as a
  // small arrow tail → signals "instant / forward / value flow".
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} fill="none" aria-label="motion-v">
      <path
        d="M7 10 L16 23 L23 10 L27 5"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
    </svg>
  );
}

function GlyphLedger({ size = 32, color = BRAND_ACCENT, stroke = 2.4 }) {
  // Four strokes from the top edge converging to a single point at the
  // bottom-centre → "six players, one ledger" concept compressed into a V.
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} fill="none" aria-label="ledger-v">
      <path d="M6 8 L16 24"  stroke={color} strokeWidth={stroke} strokeLinecap="round" />
      <path d="M12 8 L16 24" stroke={color} strokeWidth={stroke} strokeLinecap="round" />
      <path d="M20 8 L16 24" stroke={color} strokeWidth={stroke} strokeLinecap="round" />
      <path d="M26 8 L16 24" stroke={color} strokeWidth={stroke} strokeLinecap="round" />
    </svg>
  );
}

// ─────────────── CHECKMARK-FORWARD GLYPHS ────────────────────
// Refined v2 candidates — bolder strokes (~14% canvas, matching Brex/Linear)
// and asymmetric proportions so the V reads first as a checkmark (✓).

function GlyphCheckClassic({ size = 32, color = BRAND_ACCENT, stroke = 4.6 }) {
  // Classic checkmark proportions: short left stroke, long right stroke.
  // Pure ✓ silhouette. Highest "verified / approved" signal.
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} fill="none" aria-label="check-classic">
      <path
        d="M8 17 L14 23 L25 8"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GlyphCheckV({ size = 32, color = BRAND_ACCENT, stroke = 4.4 }) {
  // Hybrid: a near-symmetric V whose right arm extends decisively past the
  // top corner — reads as V first, ✓ second. Best balance of letter+symbol.
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} fill="none" aria-label="check-v">
      <path
        d="M7 11 L15 23 L27 4"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GlyphWedge({ size = 32, color = BRAND_ACCENT }) {
  // Solid filled checkmark — bolder than any stroke version, Vercel-tier
  // iconic. Two angled rectangles forming a tight ✓ wedge.
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} fill="none" aria-label="check-wedge">
      <path
        d="M7 15 L13 21 L14.5 22.5 L26 7 L23 4 L13.5 16 L9.5 12 Z"
        fill={color}
      />
    </svg>
  );
}

// ─────────────────────────── LOCKUP ────────────────────────────
function Lockup({ glyph, size = 32, label = "verqo", wordmarkColor = "#0f172a", boxRadius = 6 }) {
  return (
    <div className="flex items-center gap-2.5" data-testid={`lockup-${label}`}>
      <div
        className="grid place-items-center"
        style={{
          width: size,
          height: size,
          background: BRAND_FG,
          borderRadius: boxRadius,
          fontSize: size, // for the baseline text variant
        }}
      >
        {glyph}
      </div>
      <span className="font-semibold tracking-tight" style={{ color: wordmarkColor, fontSize: 18 }}>
        verqo
      </span>
    </div>
  );
}

// ─────────────────────────── FAVICON ROW ───────────────────────
function FaviconRow({ glyph, label }) {
  return (
    <div className="flex items-end gap-6">
      {[16, 24, 32, 48, 64].map((s) => (
        <div key={s} className="flex flex-col items-center gap-2">
          <div
            className="grid place-items-center"
            style={{ width: s, height: s, background: BRAND_FG, borderRadius: Math.max(2, s * 0.18), fontSize: s }}
          >
            {glyph}
          </div>
          <span className="text-[10px] uppercase tracking-widest text-slate-400">{s}px</span>
        </div>
      ))}
      <span className="ml-4 text-[11px] text-slate-500">{label}</span>
    </div>
  );
}

// ─────────────────────────── NAVBAR MOCK ───────────────────────
function NavbarMock({ glyph, size = 32 }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm">
      <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Lockup glyph={glyph} size={size} />
          <span className="ml-1 text-[11px] text-slate-500 hidden md:inline">· Wholesale, instant.</span>
        </div>
        <nav className="hidden md:flex items-center gap-7 text-[12px] text-slate-600">
          <span>The problem</span>
          <span>The OS</span>
          <span>Pricing</span>
        </nav>
        <span
          className="text-[12px] px-4 py-2 rounded-md text-white font-medium"
          style={{ background: BRAND_FG }}
        >
          Get paid today
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────── VARIANT CARD ──────────────────────
function VariantCard({ id, name, tagline, glyph, pros, cons, recommendation }) {
  const recColor = recommendation === "strong" ? "text-lime-700 bg-lime-100" : recommendation === "good" ? "text-amber-700 bg-amber-100" : "text-slate-600 bg-slate-100";
  return (
    <div className="rounded-2xl border border-slate-200 p-8 bg-white shadow-sm" data-testid={`variant-card-${id}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Variant {id}</div>
          <h3 className="text-2xl font-semibold text-slate-900 mt-1">{name}</h3>
          <p className="text-[13px] text-slate-600 mt-1">{tagline}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${recColor}`}>
          {recommendation === "strong" ? "Top pick" : recommendation === "good" ? "Solid" : "Backup"}
        </span>
      </div>

      {/* Hero glyph at large scale */}
      <div className="rounded-xl bg-slate-50 border border-slate-100 p-10 mb-6 grid place-items-center">
        <div
          className="grid place-items-center"
          style={{ width: 128, height: 128, background: BRAND_FG, borderRadius: 22 }}
        >
          {React.cloneElement(glyph, { size: 96 })}
        </div>
      </div>

      {/* Navbar mock */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Navbar — actual size</div>
        <NavbarMock glyph={glyph} />
      </div>

      {/* Favicon scaling */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-3">Favicon scale test</div>
        <FaviconRow glyph={glyph} label="" />
      </div>

      {/* Pros / Cons */}
      <div className="grid grid-cols-2 gap-4 text-[13px]">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-600 mb-2">Strengths</div>
          <ul className="space-y-1.5 text-slate-700">
            {pros.map((p, i) => <li key={i}>· {p}</li>)}
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-rose-600 mb-2">Trade-offs</div>
          <ul className="space-y-1.5 text-slate-700">
            {cons.map((c, i) => <li key={i}>· {c}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── PAGE ──────────────────────────────
export default function LogoVariantsPreview() {
  useEffect(() => { document.title = "verqo · Logo variants"; }, []);
  const [pick, setPick] = useState(null);

  return (
    <div className="min-h-screen bg-[#fafaf7]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-[1280px] mx-auto px-8 py-6">
          <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Brand · iteration</div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1">verqo logomark — 3 candidates</h1>
          <p className="text-[14px] text-slate-600 mt-2 max-w-2xl">
            Each variant is a custom-drawn SVG (not a font character) at ~68% canvas fill.
            Same dark-fg + lime accent palette. Compare against the current baseline below.
          </p>
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-8 py-12 space-y-12">
        {/* Baseline row */}
        <section className="rounded-2xl border border-dashed border-slate-300 p-8 bg-white">
          <div className="text-[11px] uppercase tracking-[0.28em] text-slate-400 mb-4">Current production · baseline</div>
          <div className="flex items-center gap-16 flex-wrap">
            <div
              className="grid place-items-center"
              style={{ width: 128, height: 128, background: BRAND_FG, borderRadius: 22, fontSize: 128 }}
            >
              <GlyphBaseline />
            </div>
            <div className="flex-1 min-w-[400px]">
              <NavbarMock glyph={<GlyphBaseline />} />
              <p className="text-[12px] text-slate-500 mt-4">
                Issue: the "v" is a generic Inter font character at ~40% canvas fill — looks small, floats, and signals nothing about the product.
              </p>
            </div>
          </div>
        </section>

        {/* Variant cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <VariantCard
            id={1}
            name="Geometric V"
            tagline="Chunky single-stroke V, sharp miter, mathematical."
            glyph={<GlyphGeometric />}
            recommendation="good"
            pros={[
              "Most familiar — reads instantly as V",
              "Engineered, infra-feel (Linear/Brex tier)",
              "Scales flawlessly to 16px favicon",
              "Lowest brand risk",
            ]}
            cons={[
              "Doesn't tell a product story",
              "Could feel generic without colour/palette doing work",
            ]}
          />

          <VariantCard
            id={2}
            name="Motion V"
            tagline="Right stroke extends past the join as an arrow tail."
            glyph={<GlyphMotion />}
            recommendation="strong"
            pros={[
              "Whispers 'instant / forward' — matches tagline",
              "Distinctive, ownable silhouette",
              "Still reads as V at small sizes",
              "Subtle, not gimmicky",
            ]}
            cons={[
              "Slightly asymmetric → trickier to balance with wordmark",
              "Tail can clip on very small favicons (we mitigated to 16px)",
            ]}
          />

          <VariantCard
            id={3}
            name="Ledger V"
            tagline="Four strokes converging to a single point — six players, one ledger."
            glyph={<GlyphLedger />}
            recommendation="backup"
            pros={[
              "Tells the product story (many → one)",
              "Distinctive — looks nothing else in fintech",
              "Beautiful at large scale (hero, app icon)",
            ]}
            cons={[
              "Loses clarity at 16px favicon — strokes merge",
              "Less obviously a 'V' — could confuse first-time viewers",
              "Higher conceptual lift to explain",
            ]}
          />
        </div>

        {/* Side-by-side navbar comparison */}
        <section className="rounded-2xl border border-slate-200 p-8 bg-white">
          <div className="text-[11px] uppercase tracking-[0.28em] text-slate-400 mb-6">Direct comparison · navbar context</div>
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 w-24">Current</span>
              <div className="flex-1"><NavbarMock glyph={<GlyphBaseline />} /></div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 w-24">Geometric</span>
              <div className="flex-1"><NavbarMock glyph={<GlyphGeometric />} /></div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 w-24">Motion</span>
              <div className="flex-1"><NavbarMock glyph={<GlyphMotion />} /></div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 w-24">Ledger</span>
              <div className="flex-1"><NavbarMock glyph={<GlyphLedger />} /></div>
            </div>
          </div>
        </section>

        {/* ─────────────── ROUND 2 — CHECKMARK-FORWARD ──────────────── */}
        <section className="border-t-2 border-dashed border-lime-300 pt-12 mt-8">
          <div className="text-[11px] uppercase tracking-[0.28em] text-lime-700 mb-2">Round 2 · checkmark refinements</div>
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 mb-3">Bolder. Asymmetric. V = ✓ verified.</h2>
          <p className="text-[14px] text-slate-600 max-w-2xl mb-10">
            Round 1's Motion V was too thin and too symmetric. These three lean fully into the
            checkmark concept with ~14% canvas-fill stroke weight (matching Brex / Linear) and
            asymmetric proportions so the V also reads as a ✓ — signalling "verified + go" (the
            literal meaning of verqo). All sharper, all bolder, all distinctively own-able.
          </p>

          {/* Competitor benchmark row */}
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-8 mb-10">
            <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500 mb-6">Weight benchmark · top fintech infra logomarks</div>
            <div className="flex items-end gap-12 flex-wrap">
              {[
                { name: "Brex", bg: "#000", fg: "#fff", glyph: <span style={{ fontFamily: "Georgia, serif", fontWeight: 900, fontSize: 64, color: "#fff", lineHeight: 1 }}>b</span> },
                { name: "Linear", bg: "#5e6ad2", fg: "#fff", glyph: <svg viewBox="0 0 100 100" width={64} height={64}><path d="M20 78 L20 22 L78 78 Z" fill="#fff"/></svg> },
                { name: "Vercel", bg: "#000", fg: "#fff", glyph: <svg viewBox="0 0 100 100" width={64} height={64}><path d="M50 14 L88 78 L12 78 Z" fill="#fff"/></svg> },
                { name: "Notion", bg: "#fff", fg: "#000", glyph: <span style={{ fontFamily: "Georgia, serif", fontWeight: 900, fontSize: 64, color: "#000", lineHeight: 1 }}>N</span> },
              ].map((c) => (
                <div key={c.name} className="flex flex-col items-center gap-2">
                  <div
                    className="grid place-items-center"
                    style={{ width: 96, height: 96, background: c.bg, borderRadius: 18, border: c.name === "Notion" ? "1px solid #e2e8f0" : "none" }}
                  >
                    {c.glyph}
                  </div>
                  <span className="text-[11px] text-slate-600 font-medium">{c.name}</span>
                </div>
              ))}
              <div className="flex flex-col items-center gap-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 self-center mb-1">vs</div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="grid place-items-center" style={{ width: 96, height: 96, background: BRAND_FG, borderRadius: 18 }}>
                  <GlyphCheckClassic size={72} stroke={5} />
                </div>
                <span className="text-[11px] text-lime-700 font-semibold">verqo · check classic</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="grid place-items-center" style={{ width: 96, height: 96, background: BRAND_FG, borderRadius: 18 }}>
                  <GlyphCheckV size={72} stroke={4.8} />
                </div>
                <span className="text-[11px] text-lime-700 font-semibold">verqo · check-v</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="grid place-items-center" style={{ width: 96, height: 96, background: BRAND_FG, borderRadius: 18 }}>
                  <GlyphWedge size={72} />
                </div>
                <span className="text-[11px] text-lime-700 font-semibold">verqo · wedge</span>
              </div>
            </div>
            <p className="text-[12px] text-slate-500 mt-6 leading-relaxed">
              All three verqo candidates match competitor weight & confidence. The Wedge variant
              is filled (Vercel-tier iconic). Check-V and Check-Classic use heavier strokes
              (~14% canvas fill) that hold up next to Brex and Linear.
            </p>
          </div>

          {/* Round 2 cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <VariantCard
              id="2A"
              name="Check Classic ✓"
              tagline="Pure asymmetric checkmark. Short left, long right. Maximum verified-signal."
              glyph={<GlyphCheckClassic />}
              recommendation="strong"
              pros={[
                "Strongest brand-story fit (verqo = verified + go)",
                "Bold strokes hold at 16px — favicon-safe",
                "Universally read as 'approved / done'",
                "Most distinctive — looks unlike any fintech",
              ]}
              cons={[
                "Pure check, doesn't read as 'V' first",
                "Loses the lockup<>wordmark letter-link",
              ]}
            />
            <VariantCard
              id="2B"
              name="Check-V Hybrid"
              tagline="Bold V whose right arm extends decisively as a checkmark tail."
              glyph={<GlyphCheckV />}
              recommendation="strong"
              pros={[
                "Reads as V AND ✓ — best of both",
                "Wordmark-letter link preserved",
                "Bold enough to match Brex/Linear weight",
                "Forward-leaning silhouette = 'instant'",
              ]}
              cons={[
                "Slightly more complex than pure forms",
                "Tail must scale carefully on tiny favicons",
              ]}
            />
            <VariantCard
              id="2C"
              name="Wedge ✓"
              tagline="Solid filled checkmark — Vercel-tier iconic confidence."
              glyph={<GlyphWedge />}
              recommendation="good"
              pros={[
                "Boldest of all — pure shape, no stroke fragility",
                "Scales perfectly to any size (incl. 16px favicon)",
                "App-icon-ready, ownable silhouette",
              ]}
              cons={[
                "Furthest from a 'V' letter — most brand reset",
                "Filled shapes feel slightly more 'logo' less 'wordmark glyph'",
              ]}
            />
          </div>

          {/* Round 2 navbar comparison */}
          <section className="rounded-2xl border border-slate-200 p-8 bg-white mt-10">
            <div className="text-[11px] uppercase tracking-[0.28em] text-slate-400 mb-6">Round 2 · navbar comparison</div>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <span className="text-[10px] uppercase tracking-widest text-slate-400 w-32">Current</span>
                <div className="flex-1"><NavbarMock glyph={<GlyphBaseline />} /></div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-[10px] uppercase tracking-widest text-lime-700 w-32 font-semibold">Check Classic</span>
                <div className="flex-1"><NavbarMock glyph={<GlyphCheckClassic />} /></div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-[10px] uppercase tracking-widest text-lime-700 w-32 font-semibold">Check-V Hybrid</span>
                <div className="flex-1"><NavbarMock glyph={<GlyphCheckV />} /></div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-[10px] uppercase tracking-widest text-lime-700 w-32 font-semibold">Wedge</span>
                <div className="flex-1"><NavbarMock glyph={<GlyphWedge />} /></div>
              </div>
            </div>
          </section>
        </section>

        {/* Picker */}
        <section className="rounded-2xl bg-[#0c1612] text-white p-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-lime-300 mb-3">Pick one</div>
          <h2 className="text-3xl font-semibold tracking-tight mb-6">Which variant should we ship?</h2>
          <div className="flex flex-wrap gap-3 mb-6">
            {[
              { id: "geometric",     label: "1 · Geometric V",   group: "r1" },
              { id: "motion",        label: "2 · Motion V",      group: "r1" },
              { id: "ledger",        label: "3 · Ledger V",      group: "r1" },
              { id: "check-classic", label: "2A · Check Classic ✓", group: "r2" },
              { id: "check-v",       label: "2B · Check-V Hybrid", group: "r2" },
              { id: "wedge",         label: "2C · Wedge ✓",      group: "r2" },
              { id: "keep",          label: "Keep current",      group: "x" },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setPick(opt.id)}
                data-testid={`pick-${opt.id}`}
                className={`px-5 py-3 rounded-full text-[13px] font-medium transition-all ${
                  pick === opt.id
                    ? "bg-lime-300 text-[#0c1612]"
                    : opt.group === "r2"
                    ? "bg-lime-300/20 text-lime-200 hover:bg-lime-300/30 border border-lime-300/30"
                    : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {pick && (
            <p className="text-[14px] text-white/70" data-testid="pick-confirmation">
              Selected: <span className="text-lime-300 font-medium">{pick}</span> · message the agent "I pick {pick}" to roll it out across `VerqoLockup`, favicons, and og:image.
            </p>
          )}
        </section>
      </main>
    </div>
  );
}
